import React from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline'

// Membership templates based on business type
const membershipTemplates = {
  ice_rink: [
    { 
      name: 'Basic Skater', 
      price: 39, 
      period: 'monthly',
      benefits: ['10% off public skate', 'Priority registration', 'Monthly newsletter']
    },
    { 
      name: 'Frequent Skater', 
      price: 79, 
      period: 'monthly',
      benefits: ['Unlimited public skate', '20% off lessons', 'Guest passes (2/month)', 'Skate rental included']
    },
    { 
      name: 'VIP Pass', 
      price: 149, 
      period: 'monthly',
      benefits: ['All Frequent Skater benefits', 'Free lessons (2/month)', 'Priority ice time booking', 'Exclusive member events']
    }
  ],
  training_center: [
    { 
      name: 'Basic Membership', 
      price: 49, 
      period: 'monthly',
      benefits: ['Gym access', 'Basic equipment', 'Locker room access']
    },
    { 
      name: 'Premium Membership', 
      price: 99, 
      period: 'monthly',
      benefits: ['All Basic benefits', 'Group classes included', 'Guest passes (2/month)', 'Nutrition app access']
    },
    { 
      name: 'Elite Membership', 
      price: 199, 
      period: 'monthly',
      benefits: ['All Premium benefits', 'Personal training (2 sessions)', 'Recovery room access', 'Priority booking']
    }
  ],
  sports_complex: [
    { 
      name: 'Individual Player', 
      price: 29, 
      period: 'monthly',
      benefits: ['Drop-in game access', '10% off rentals', 'Online booking']
    },
    { 
      name: 'Team Captain', 
      price: 199, 
      period: 'seasonal',
      benefits: ['Team registration', 'Priority field booking', 'Equipment storage', 'Team stats tracking']
    },
    { 
      name: 'Facility Partner', 
      price: 499, 
      period: 'monthly',
      benefits: ['Unlimited facility access', '50% off all rentals', 'Dedicated support', 'Custom events']
    }
  ],
  dance_martial_arts: [
    { 
      name: 'Drop-In Pass', 
      price: 99, 
      period: 'monthly',
      benefits: ['5 classes per month', 'Make-up classes', 'Member events']
    },
    { 
      name: 'Unlimited Classes', 
      price: 149, 
      period: 'monthly',
      benefits: ['Unlimited classes', 'Workshop discounts', 'Guest passes', 'Priority enrollment']
    },
    { 
      name: 'Competition Team', 
      price: 249, 
      period: 'monthly',
      benefits: ['All class access', 'Competition entry fees', 'Private coaching', 'Costume/uniform included']
    }
  ],
  other: []
}

export default function MembershipStep() {
  const { wizardData, updateStepData } = useSetupWizard()
  const colors = useThemeColors()
  const { memberships, organizationProfile } = wizardData

  const addMembership = () => {
    const newMembership = {
      id: `membership-${Date.now()}`,
      name: '',
      price: 0,
      period: 'monthly',
      benefits: [],
      description: ''
    }
    updateStepData('memberships', [...memberships, newMembership])
  }

  const updateMembership = (id: string, field: string, value: string | number | string[]) => {
    const updated = memberships.map(membership =>
      membership.id === id ? { ...membership, [field]: value } : membership
    )
    updateStepData('memberships', updated)
  }

  const removeMembership = (id: string) => {
    const updated = memberships.filter(membership => membership.id !== id)
    updateStepData('memberships', updated)
  }

  const addBenefit = (membershipId: string, benefit: string) => {
    if (benefit.trim()) {
      const membership = memberships.find(m => m.id === membershipId)
      if (membership) {
        updateMembership(membershipId, 'benefits', [...(membership.benefits || []), benefit.trim()])
      }
    }
  }

  const removeBenefit = (membershipId: string, benefitIndex: number) => {
    const membership = memberships.find(m => m.id === membershipId)
    if (membership) {
      const updatedBenefits = membership.benefits?.filter((_, index) => index !== benefitIndex) || []
      updateMembership(membershipId, 'benefits', updatedBenefits)
    }
  }

  const loadTemplates = () => {
    const templates = membershipTemplates[organizationProfile.businessType] || []
    const newMemberships = templates.map((template, index) => ({
      id: `membership-${Date.now()}-${index}`,
      ...template
    }))
    updateStepData('memberships', newMemberships)
  }

  return (
    <div className="membership-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Create membership tiers (optional)
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Offer recurring memberships to provide value to your regular customers and create predictable revenue.
      </p>

      {/* Template Suggestion */}
      {memberships.length === 0 && organizationProfile.businessType !== 'other' && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}30`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.text,
            margin: 0
          }}>
            💡 Want to start with membership tiers for your business type?
          </p>
          <button
            onClick={loadTemplates}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.813rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Use Template
          </button>
        </div>
      )}

      {/* Skip Note */}
      {memberships.length === 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: colors.backgroundLight,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.textSecondary,
            margin: 0
          }}>
            This step is optional. You can skip it and add memberships later from your settings.
          </p>
        </div>
      )}

      {/* Memberships List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {memberships.map((membership, index) => (
          <div
            key={membership.id}
            style={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '1.5rem'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: colors.text,
                margin: 0
              }}>
                Tier {index + 1}
              </h3>
              <button
                onClick={() => removeMembership(membership.id)}
                style={{
                  padding: '0.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.error,
                  cursor: 'pointer'
                }}
              >
                <TrashIcon style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {/* Membership Name */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.813rem',
                  fontWeight: '500',
                  marginBottom: '0.25rem',
                  color: colors.textSecondary
                }}>
                  Membership Name
                </label>
                <input
                  type="text"
                  value={membership.name}
                  onChange={(e) => updateMembership(membership.id, 'name', e.target.value)}
                  placeholder="e.g., Premium Membership"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Price and Period */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Price ($)
                  </label>
                  <input
                    type="number"
                    value={membership.price}
                    onChange={(e) => updateMembership(membership.id, 'price', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Billing Period
                  </label>
                  <select
                    value={membership.period || 'monthly'}
                    onChange={(e) => updateMembership(membership.id, 'period', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>

              {/* Benefits */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.813rem',
                  fontWeight: '500',
                  marginBottom: '0.25rem',
                  color: colors.textSecondary
                }}>
                  Benefits
                </label>
                <div style={{
                  marginBottom: '0.5rem'
                }}>
                  {membership.benefits?.map((benefit, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0',
                        color: colors.text,
                        fontSize: '0.875rem'
                      }}
                    >
                      <CheckIcon style={{ 
                        width: '16px', 
                        height: '16px', 
                        color: colors.primary,
                        flexShrink: 0
                      }} />
                      <span style={{ flex: 1 }}>{benefit}</span>
                      <button
                        onClick={() => removeBenefit(membership.id, i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          padding: '0 0.25rem',
                          fontSize: '1.25rem',
                          lineHeight: '1'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add a benefit (press Enter)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const input = e.target as HTMLInputElement
                      addBenefit(membership.id, input.value)
                      input.value = ''
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.813rem'
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Membership Button */}
      <button
        onClick={addMembership}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: 'transparent',
          border: `2px dashed ${colors.border}`,
          borderRadius: '8px',
          color: colors.primary,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primary
          e.currentTarget.style.backgroundColor = `${colors.primary}10`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <PlusIcon style={{ width: '20px', height: '20px' }} />
        Add Membership Tier
      </button>
    </div>
  )
}